import React, {
    createContext,
    useState,
    useEffect,
    ReactNode,
    useCallback,
    useRef,
} from 'react';
import axios from '../api/axiosConfig';
import { CanceledError } from 'axios';

type Role = 'viewer' | 'editor';

interface AuthContextType {
    email: string | null;
    role: Role | null;
    loading: boolean;
    login: () => void;
    logout: () => void;
    isLoggedIn: () => boolean;
}

export const AuthContext = createContext<AuthContextType>({
    email: null,
    role: null,
    loading: true,
    login: () => {},
    logout: () => {},
    isLoggedIn: () => false,
});

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const [email, setEmail] = useState<string | null>(null);
    const [role, setRole] = useState<Role | null>(null);
    const [loading, setLoading] = useState(true);

    // Prevent duplicate redirects
    const isRedirectingRef = useRef(false);
    const lastCheckRef = useRef(0);
    const isCheckingRef = useRef(false);

    // handles unathenticated state
    const forceUnauthenticated = useCallback(() => {
        // Clear local auth state
        setEmail(null);
        setRole(null);

        // If already on /login, do nothing
        if (window.location.pathname.startsWith('/login')) return;

        // Avoid double redirects
        if (isRedirectingRef.current) return;
        isRedirectingRef.current = true;

        // Go to /login with a return URL to where the user was
        const redirect = encodeURIComponent(
            window.location.pathname + window.location.search
        );
        window.location.replace(`/login?redirect=${redirect}`);
    }, []);

    const checkAuthQuick = useCallback(async () => {
        if (window.location.pathname.startsWith('/login')) return;

        try {
            const res = await axios.get('/api/users/me', {
                validateStatus: () => true,
            });
            if (res.status === 401 || !res.data?.email) {
                forceUnauthenticated();
            }
        } catch {
            // network error -> ignore; next interaction will re-check
        }
    }, [forceUnauthenticated]);

    // On mount: check session via httponly cookie
    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get<{ email: string; role: Role }>(
                    '/api/users/me'
                );
                setEmail(data.email);
                setRole(data.role);
            } catch {
                setEmail(null);
                setRole(null);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const login = useCallback(() => {
        // Read redirect (if user landed on /login?redirect=...)
        const params = new URLSearchParams(window.location.search);
        const redirect = params.get('redirect') || '/';

        // Tell the backend what to remember; it will store it in a short-lived cookie
        window.location.href = `/api/auth/azure/login?redirect=${encodeURIComponent(redirect)}`;
    }, []);

    const logout = useCallback(async () => {
        try {
            await axios.post('/api/auth/azure/logout');
        } catch {
            // swallow
        } finally {
            setEmail(null);
            setRole(null);
        }
    }, []);

    useEffect(() => {
        const id = axios.interceptors.response.use(
            (r) => r,
            (error) => {
                const status = error?.response?.status;
                if (status === 401) {
                    forceUnauthenticated();
                    // Stop downstream logic after redirect decision
                    return Promise.reject(
                        new CanceledError('Redirecting to login')
                    );
                }
                return Promise.reject(error);
            }
        );
        return () => {
            axios.interceptors.response.eject(id);
        };
    }, [forceUnauthenticated]);

    useEffect(() => {
        const MIN_MS_BETWEEN_CHECKS = 15000; // 15s throttle

        const maybeCheck = () => {
            const now = Date.now();
            if (isCheckingRef.current) return;
            if (now - lastCheckRef.current < MIN_MS_BETWEEN_CHECKS) return;

            isCheckingRef.current = true;
            checkAuthQuick().finally(() => {
                lastCheckRef.current = Date.now();
                isCheckingRef.current = false;
            });
        };

        const captureOptions: AddEventListenerOptions = { capture: true };

        // Run on any user interaction that matters + when the window regains focus
        window.addEventListener('click', maybeCheck, captureOptions);
        window.addEventListener('focus', maybeCheck);
        document.addEventListener('submit', maybeCheck, captureOptions);

        return () => {
            window.removeEventListener('click', maybeCheck, captureOptions);
            window.removeEventListener('focus', maybeCheck);
            document.removeEventListener('submit', maybeCheck, captureOptions);
        };
    }, [checkAuthQuick]);

    const isLoggedIn = useCallback(() => !!email && !!role, [email, role]);

    return (
        <AuthContext.Provider
            value={{ email, role, loading, login, logout, isLoggedIn }}
        >
            {children}
        </AuthContext.Provider>
    );
};
